// BookingAutomationDebug — the Debug-tab section listing every scheduled
// automation message for a booking with a body preview (exactly what the send
// path will produce / actually produced) and row actions: cancel a pending
// send, retry a failed/cancelled one. Actions are gated on automation.write.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bookingAutomationMessagesQuery,
  cancelScheduledMessage,
  retryScheduledMessage,
  type BookingAutomationMessage,
} from '@/lib/scheduled';
import { messageFromError } from '@/lib/api';
import { usePermissions } from '@/lib/me';
import { useToast } from '@/components/ToastProvider';
import { useConfirm } from '@/components/DialogProvider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface Props {
  bookingId: string;
}

interface BadgeSpec {
  label: string;
  variant: 'success' | 'neutral' | 'warning' | 'danger' | 'gold';
}

const STATUS_BADGE: Record<BookingAutomationMessage['status'], BadgeSpec> = {
  pending: { label: 'Pending', variant: 'gold' },
  in_progress: { label: 'Sending…', variant: 'warning' },
  sent: { label: 'Sent', variant: 'success' },
  failed: { label: 'Failed', variant: 'danger' },
  cancelled: { label: 'Cancelled', variant: 'neutral' },
};

// A status value this bundle does not know yet must not take down the tab.
function statusBadge(status: BookingAutomationMessage['status']): BadgeSpec {
  return STATUS_BADGE[status] ?? { label: status, variant: 'neutral' };
}

const SOURCE_HINT: Record<BookingAutomationMessage['preview_source'], string> = {
  live: 'Rendered now from the current template with the frozen variables — what will be sent.',
  frozen: 'Body frozen at scheduling time (a note explains any degradation).',
  sent: 'The message body that was actually delivered.',
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function BookingAutomationDebug({ bookingId }: Props) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const { can } = usePermissions();
  const canWrite = can('automation.write');
  const canRead = can('automation.read');

  const { data, isLoading, isError, error, refetch } = useQuery({
    ...bookingAutomationMessagesQuery(bookingId),
    enabled: !!bookingId && canRead,
  });
  const items = data?.items ?? [];
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['bookings', bookingId, 'automation-messages'] });

  const cancelMut = useMutation({
    mutationFn: (id: string) =>
      cancelScheduledMessage({ throwOnError: true, path: { id } }),
    onSuccess: () => {
      toast({ kind: 'success', title: 'Message cancelled' });
      void invalidate();
    },
    onError: (err) => {
      toast({ kind: 'error', title: 'Cancel failed', message: messageFromError(err) });
      void invalidate(); // the row likely changed state under us — refresh
    },
  });

  const retryMut = useMutation({
    mutationFn: (id: string) =>
      retryScheduledMessage({ throwOnError: true, path: { id } }),
    onSuccess: () => {
      toast({ kind: 'success', title: 'Message queued for retry' });
      void invalidate();
    },
    onError: (err) => {
      toast({ kind: 'error', title: 'Retry failed', message: messageFromError(err) });
      void invalidate();
    },
  });

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  // The endpoint requires automation.read — without it, showing the section
  // would only render a misleading error.
  if (!canRead) return null;

  return (
    <section className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <h2 className="mb-3 text-sm font-semibold text-[var(--text-heading)]">
        Automation messages
      </h2>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}
      {isError && (
        <div className="flex items-center gap-3">
          <p role="alert" className="text-sm text-destructive">
            {messageFromError(error)}
          </p>
          <Button size="sm" variant="secondary" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      )}
      {!isLoading && !isError && items.length === 0 && (
        <p className="text-sm text-muted">No automation messages for this booking.</p>
      )}

      <div className="space-y-3">
        {items.map((item) => {
          const badge = statusBadge(item.status);
          const isOpen = expanded.has(item.id);
          return (
            <div
              key={item.id}
              className="rounded-[var(--radius-control)] border border-[var(--border-soft)] p-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={badge.variant} dot>
                  {item.status === 'cancelled' && item.operator_cancelled
                    ? 'Cancelled (operator)'
                    : badge.label}
                </Badge>
                {item.rule_active === false && item.status === 'pending' && (
                  <Badge
                    variant="warning"
                    title="The rule is disabled — the worker will cancel this row instead of sending."
                  >
                    rule disabled — will not send
                  </Badge>
                )}
                <span className="text-sm font-medium">
                  {item.rule_name ?? `${item.rule_id.slice(0, 8)}…`}
                </span>
                {item.rule_trigger && (
                  <span className="text-xs text-muted">({item.rule_trigger})</span>
                )}
                <span className="text-xs text-muted">·</span>
                <span className="text-xs text-muted">
                  {item.template_name ?? `${item.template_id.slice(0, 8)}…`}
                </span>
                <span className="ml-auto text-xs tabular-nums text-muted">
                  {item.status === 'sent' && item.sent_at
                    ? `sent ${formatDateTime(item.sent_at)}`
                    : formatDateTime(item.scheduled_for)}
                </span>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button size="sm" variant="secondary" onClick={() => toggle(item.id)}>
                  {isOpen ? 'Hide preview' : 'Preview'}
                </Button>
                {canWrite && item.status === 'pending' && (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={cancelMut.isPending}
                    onClick={async () => {
                      const ok = await askConfirm({
                        title: 'Cancel this scheduled message?',
                        destructive: true,
                        confirmLabel: 'Cancel message',
                      });
                      if (ok) cancelMut.mutate(item.id);
                    }}
                  >
                    Cancel
                  </Button>
                )}
                {canWrite &&
                  (item.status === 'failed' || item.status === 'cancelled') &&
                  // A row whose delivered body exists was actually sent (a lost
                  // MarkSent write) — a retry would be a confusing no-op.
                  item.preview_source !== 'sent' && (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={retryMut.isPending}
                    onClick={() => retryMut.mutate(item.id)}
                  >
                    Retry
                  </Button>
                )}
                {item.attempts > 0 && (
                  <span className="text-xs text-muted">attempts: {item.attempts}</span>
                )}
                {item.last_error && (
                  <span
                    className="max-w-[24rem] truncate text-xs text-destructive"
                    title={item.last_error}
                  >
                    {item.last_error}
                  </span>
                )}
              </div>

              {isOpen && (
                <div className="mt-3 rounded-[var(--radius-control)] bg-[var(--surface-sunken)] p-3">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <Badge variant="neutral" title={SOURCE_HINT[item.preview_source]}>
                      {item.preview_source}
                    </Badge>
                    {item.preview_note && (
                      <span className="text-xs text-[var(--ollio-warning,#b45309)]">
                        {item.preview_note}
                      </span>
                    )}
                  </div>
                  <pre className="whitespace-pre-wrap break-words font-sans text-sm text-foreground">
                    {item.preview_body ?? '— no body —'}
                  </pre>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
